import LegalDocShell from "@/components/legal/LegalDocShell";
import { buildLegalSections } from "@/lib/legalRenderer";

const SECTIONS = buildLegalSections([
  { id: "purpose", title: "1. Purpose of NaturePulse Data", body:
`NaturePulse is intended to create a growing record of what people observe in the natural and agricultural world. Examples may include:
- Flowering dates
- Fruit formation
- Plant growth
- Crop planting
- Crop harvesting
- Bird sightings
- Nesting activity
- Wildlife observations
- Insect and pollinator activity
- Pest outbreaks
- Plant diseases
- Rainfall
- Drought
- Flooding
- Soil conditions
- Water conditions
- Forest changes
- Habitat changes
- Coastal conditions
- Seasonal changes
- Weather observations
- Agricultural conditions
- Other environmental observations

A single observation may have limited scientific value. Thousands or millions of observations collected across time and geography may help reveal meaningful patterns.`
  },
  { id: "citizen-science", title: "2. Citizen Science", body:
`NaturePulse supports the concept of citizen science. Citizen science allows members of the public to contribute observations that may help support:
- Scientific research
- Environmental monitoring
- Agriculture
- Conservation
- Education
- Climate studies
- Biodiversity research
- Historical analysis
- Community planning
- Public awareness

Participation in NaturePulse does not automatically mean a user's observation is scientifically verified.`
  },
  { id: "types", title: "3. Types of Data", body:
`NaturePulse data may include several categories.

## Observation Data
Species or crop, date, time, general location, weather, environmental conditions, description, tags, photographs, measurements, community identifications.

## Agricultural Data
Farmers Pulse may include: crop type, planting date, flowering date, harvest date, pest observations, disease observations, soil observations, rainfall, irrigation, harvest information, other agricultural observations.

## Geographic Data
Country, state or province, district or county, city, town, village, general area, approximate coordinates, precise GPS coordinates where authorized.

## User Information
Username, display name, contributor profile, contributor ID. Personally identifying information will be handled according to the NaturePulse Privacy Policy.`
  },
  { id: "public", title: "4. Public Data", body:
`Information intentionally designated by users as public may be used to support NaturePulse's public features. Public observation data may appear in:
- Observation pages
- Maps
- Timelines
- Species pages
- Crop pages
- Geographic pages
- Search results
- Statistical summaries
- Educational resources
- Research datasets
- Public reports

Public visibility does not automatically mean that all intellectual property rights are waived. Photographs and original creative content remain subject to applicable ownership and licensing terms.`
  },
  { id: "private", title: "5. Private Data", body:
`Information marked private will not ordinarily be included in public research datasets. Private data may still be processed internally when reasonably necessary to:
- Operate the Service
- Maintain records
- Generate private analytics
- Provide requested features
- Protect security
- Improve technical performance

Private information will be handled according to the Privacy Policy.`
  },
  { id: "aggregated", title: "6. Aggregated Data", body:
`NaturePulse may create aggregated information by combining observations from multiple users. Examples may include:
- Number of bird sightings in a region
- Average flowering date
- Rainfall observations by district
- Pest reports by crop
- Crop planting activity by month
- Number of flood reports
- Pollinator activity by season

Aggregated data is generally intended to describe patterns rather than individual users.`
  },
  { id: "deidentified", title: "7. De-Identified and Anonymized Data", body:
`Where appropriate, NaturePulse may remove or reduce identifying information before data is used for research or external analysis. This may include:
- Removing names
- Replacing account identifiers
- Reducing geographic precision
- Removing contact information
- Combining observations
- Generalizing dates
- Applying statistical techniques

NaturePulse will seek to reduce the risk of re-identification where de-identified information is shared.`
  },
  { id: "research-uses", title: "8. Research Uses", body:
`NaturePulse data may be used to support research involving:
- Biodiversity
- Climate change
- Agriculture
- Crop cycles
- Plant phenology
- Bird migration
- Wildlife distribution
- Pollinator activity
- Pest movement
- Plant diseases
- Drought
- Flooding
- Water availability
- Soil conditions
- Forest health
- Habitat change
- Environmental degradation
- Seasonal variation
- Urban ecology
- Coastal change
- Conservation
- Agricultural resilience

This list is illustrative and may expand over time.`
  },
  { id: "farmers-pulse", title: "9. Farmers Pulse Research", body:
`Farmers Pulse observations may be especially useful for understanding changes in agriculture. Research topics may include:
- Planting dates
- Harvest dates
- Yield trends
- Changing growing seasons
- Pest movement
- Crop diseases
- Water shortages
- Flood impacts
- Soil conditions
- Pollinator changes
- Heat stress
- Drought stress
- Crop adaptation
- Climate-resilient agriculture
- Regional crop patterns

Farm data used for research should be handled with appropriate attention to privacy and commercial sensitivity.`
  },
  { id: "who-may-use", title: "10. Who May Use NaturePulse Data?", body:
`NaturePulse data may be made available, where appropriate, to:
- Students
- Teachers
- Schools
- Universities
- Researchers
- Agricultural institutions
- Extension services
- Government agencies
- Environmental organizations
- Conservation organizations
- Nonprofit organizations
- Community groups
- Journalists
- Public-interest organizations
- Other qualified users

Access may depend on the type of data requested.`
  },
  { id: "access-levels", title: "11. Research Access Levels", body:
`NaturePulse may establish different levels of data access. Examples may include:

## Public Access
Data visible directly through NaturePulse.

## Downloadable Public Dataset
Selected public observations made available for download.

## Research Access
Additional datasets available to approved researchers.

## Restricted Access
Data requiring additional review because it may contain sensitive information.

## Private Data
Not available for external research unless specifically authorized or legally permitted.`
  },
  { id: "requests", title: "12. Research Requests", body:
`Researchers seeking data not publicly available may be required to submit a request describing:
- Researcher name
- Institution
- Research purpose
- Dataset requested
- Geographic scope
- Time period
- Intended use
- Data-security practices
- Publication plans
- Funding source
- Whether results will be commercially used

NaturePulse may approve, deny, or limit research access.`
  },
  { id: "ethics", title: "13. Research Ethics", body:
`Researchers using NaturePulse data should follow appropriate ethical standards. Where applicable, researchers should consider:
- Institutional review requirements
- Human-subject protections
- Privacy
- Indigenous data considerations
- Farm confidentiality
- Sensitive species protection
- Data security
- Responsible publication

NaturePulse may require additional safeguards for sensitive projects.`
  },
  { id: "sensitive-species", title: "14. Sensitive Species Data", body:
`Exact locations of sensitive species may be restricted. Examples include:
- Endangered species
- Rare orchids
- Nesting sites
- Breeding areas
- Species vulnerable to poaching
- Protected habitats

Researchers may be required to demonstrate a legitimate need before receiving precise location information. NaturePulse may provide generalized locations instead.`
  },
  { id: "sensitive-farms", title: "15. Sensitive Farm Data", body:
`Farmers Pulse information may contain commercially or personally sensitive information. Examples include:
- Exact farm coordinates
- Production levels
- Yield
- Infrastructure
- Irrigation sources
- Livestock locations
- Crop locations

NaturePulse may limit access to such information or provide aggregated or generalized versions.`
  },
  { id: "attribution", title: "16. Attribution", body:
`Researchers using NaturePulse data should provide appropriate attribution where required. Suggested attribution may include:

"Data source: NaturePulse.net, a division of Social Pulse Inc."

Where appropriate, researchers may also acknowledge NaturePulse contributors. Specific datasets may include their own required citation format.`
  },
  { id: "contributor-attribution", title: "17. Contributor Attribution", body:
`Public contributors may be credited using their chosen display name where appropriate. However, NaturePulse may remove contributor identifiers from research datasets when necessary to protect privacy.`
  },
  { id: "photographs", title: "18. Photographs in Research", body:
`Photographs remain subject to applicable copyright and licensing terms. Access to observation data does not automatically grant permission to reproduce photographs in:
- Publications
- Books
- Advertising
- Commercial products
- Presentations
- Documentaries

Researchers should verify the applicable license before reproducing media.`
  },
  { id: "commercial", title: "19. Commercial Research", body:
`NaturePulse may distinguish between:
- Academic research
- Nonprofit research
- Government research
- Public-interest use
- Commercial research

Commercial organizations may be subject to separate terms, licenses, fees, or access restrictions. NaturePulse may introduce commercial data licenses in the future.`
  },
  { id: "prohibited", title: "20. Prohibited Uses", body:
`NaturePulse data must not be used to facilitate:
- Poaching
- Illegal wildlife collection
- Illegal logging
- Theft from farms
- Harassment
- Stalking
- Discrimination
- Re-identification of protected users
- Unauthorized surveillance
- Fraud
- Manipulation of agricultural markets
- Other unlawful activities

NaturePulse may restrict access where misuse is suspected.`
  },
  { id: "no-reid", title: "21. No Re-Identification", body:
`Users of de-identified datasets should not attempt to identify individual contributors unless authorized.

This includes attempting to combine NaturePulse data with external datasets to determine:
- Home addresses
- Exact private farm locations
- Individual identities
- Private travel patterns
- Other sensitive information`
  },
  { id: "no-exploit", title: "22. No Sensitive Location Exploitation", body:
`Researchers and other users must not exploit location data to harm:
- Wildlife
- Farms
- Private property
- Conservation areas
- Indigenous lands
- Sensitive ecosystems`
  },
  { id: "quality", title: "23. Data Quality", body:
`NaturePulse includes user-contributed observations. Data may contain:
- Errors
- Misidentifications
- Incomplete information
- Duplicate observations
- Incorrect coordinates
- Incorrect dates
- AI-generated suggestions
- Unverified reports

Researchers must independently assess data quality before using it.`
  },
  { id: "verification", title: "24. Verification Levels", body:
`NaturePulse may identify observations using verification levels such as:
- Unverified
- AI Suggested
- Community Suggested
- Community Verified
- Expert Verified
- Research Grade

Researchers should consider verification status when designing studies.`
  },
  { id: "corrections", title: "25. Corrections", body:
`NaturePulse observations may change over time as:
- Identifications are corrected
- Locations are refined
- Community verification occurs
- Experts review observations
- Users edit their submissions

Researchers should document the date and version of datasets used.`
  },
  { id: "historical", title: "26. Historical Data", body:
`NaturePulse aims to preserve long-term environmental observations. Historical data may be particularly useful for analyzing:
- Seasonal changes
- Climate trends
- Species distribution
- Crop calendars
- Agricultural adaptation
- Habitat change

NaturePulse may preserve older observations even when platform features evolve.`
  },
  { id: "downloads", title: "27. Data Downloads", body:
`NaturePulse may provide downloadable data formats such as:
- CSV
- JSON
- Spreadsheet
- GeoJSON
- API output
- Other formats

Availability may vary by dataset.`
  },
  { id: "apis", title: "28. APIs", body:
`NaturePulse may eventually provide APIs for approved data access. API users may be subject to additional:
- Rate limits
- Authentication
- Licensing
- Attribution
- Security requirements
- Usage restrictions

Unauthorized scraping may be prohibited.`
  },
  { id: "automated", title: "29. Automated Collection", body:
`Users should not automatically scrape, crawl, or harvest NaturePulse data in violation of applicable terms. NaturePulse may provide approved APIs or export mechanisms for legitimate use.`
  },
  { id: "ai-research", title: "30. Artificial Intelligence and Research", body:
`NaturePulse may use AI to help:
- Categorize observations
- Identify species
- Detect patterns
- Generate summaries
- Find anomalies
- Support research discovery

AI-generated classifications may contain errors. Researchers should validate AI-generated information where accuracy matters.`
  },
  { id: "publications", title: "31. Research Publications", body:
`NaturePulse encourages researchers to share findings derived from NaturePulse data. Where practical, researchers may be asked to provide:
- Publication citation
- Research summary
- Link to published work
- Data methodology
- Corrections discovered during analysis

NaturePulse may link to resulting research from appropriate pages.`
  },
  { id: "community-benefit", title: "32. Community Benefit", body:
`Where possible, research using NaturePulse data should contribute knowledge back to the community. Examples include:
- Public summaries
- Educational materials
- Maps
- Findings
- Recommendations
- Research articles

NaturePulse encourages accessible communication of research results.`
  },
  { id: "government", title: "33. Government Use", body:
`Government agencies may use appropriate NaturePulse data for purposes such as:
- Environmental monitoring
- Agriculture
- Conservation
- Planning
- Disaster assessment
- Research

NaturePulse data should not be treated as authoritative government data unless independently verified.`
  },
  { id: "education", title: "34. Educational Use", body:
`Schools and universities may use publicly available NaturePulse information for:
- Classroom activities
- Citizen-science projects
- Environmental education
- Agricultural education
- Research assignments

Users should follow applicable attribution and copyright requirements.`
  },
  { id: "indigenous", title: "35. Indigenous and Traditional Knowledge", body:
`NaturePulse recognizes that certain observations may include traditional or Indigenous ecological knowledge. Such information may require additional cultural, ethical, or legal protections. NaturePulse may restrict use of sensitive traditional knowledge where appropriate.

Researchers should respect applicable community protocols.`
  },
  { id: "security", title: "36. Data Security", body:
`Researchers receiving restricted NaturePulse data may be required to:
- Limit authorized access
- Use secure systems
- Encrypt sensitive data
- Avoid unauthorized redistribution
- Delete data after an agreed period
- Report security incidents

NaturePulse may require a separate data-use agreement.`
  },
  { id: "retention", title: "37. Data Retention by Researchers", body:
`Researchers should retain NaturePulse data only as long as reasonably necessary for their approved purpose. Restricted datasets may have specific retention requirements.`
  },
  { id: "redistribution", title: "38. Redistribution", body:
`Public datasets may be redistributed only in accordance with the applicable license. Restricted datasets must not be redistributed without authorization.`
  },
  { id: "changes-datasets", title: "39. Changes to Datasets", body:
`NaturePulse may update, correct, expand, replace, or discontinue datasets. Users should not assume that a dataset will remain unchanged indefinitely.`
  },
  { id: "availability", title: "40. No Guarantee of Availability", body:
`NaturePulse does not guarantee permanent availability of:
- Research datasets
- APIs
- Download tools
- Historical archives
- Specific observations

Researchers should maintain appropriate copies of authorized datasets used in their work.`
  },
  { id: "no-warranty", title: "41. No Warranty", body:
`NaturePulse data is provided for informational and research purposes. Unless expressly stated otherwise, NaturePulse does not guarantee that data is:
- Accurate
- Complete
- Current
- Scientifically validated
- Error-free
- Suitable for a particular purpose

Researchers remain responsible for interpreting and validating data.`
  },
  { id: "reporting", title: "42. Reporting Research Concerns", body:
`Users may report concerns involving:
- Misuse of NaturePulse data
- Privacy violations
- Sensitive wildlife exposure
- Farm confidentiality
- Improper research practices
- Unauthorized redistribution

Reports may be sent to: info@naturepulse.net`
  },
  { id: "changes", title: "43. Changes to This Policy", body:
`NaturePulse may update this Research & Data Use Policy as:
- Research programs expand
- New datasets become available
- APIs are introduced
- Privacy laws change
- New partnerships develop
- Technology evolves

Material changes may be communicated through NaturePulse.net.`
  },
  { id: "contact", title: "44. Contact Us", body:
`Research or data questions may be directed to:

NaturePulse.net, a division of Social Pulse Inc.
Website: [NaturePulse.net](https://naturepulse.net)
Research/Data Email: info@naturepulse.net
Privacy Email: legal@naturepulse.net
Support Email: support@naturepulse.net

Mailing Address:
Social Pulse Inc.
15166 SE Aston Loop
Portland, OR 97236
United States`
  },
]);

export default function ResearchPolicy() {
  return (
    <LegalDocShell
      eyebrow="NaturePulse · Research & Data Use"
      title="Research & Data Use Policy"
      effective="August 23, 2026"
      updated="August 23, 2026"
      intro={
        <>
          <p>NaturePulse.net, a division of Social Pulse Inc. ("NaturePulse," "we," "us," or "our") is designed to help people document, organize, and better understand observations about nature, agriculture, biodiversity, weather, climate, wildlife, habitats, and environmental change.</p>
          <p>NaturePulse may also make certain information available for educational, scientific, agricultural, conservation, historical, and public-interest purposes.</p>
          <p>This Policy should be read together with the <a href="/legal/terms">Terms of Service</a>, Privacy Policy, <a href="/legal/user-content">User-Generated Content Policy</a>, <a href="/legal/community">Community Guidelines</a>, <a href="/legal/cookies">Cookie Policy</a>, and <a href="/legal/ai">AI Use &amp; Agricultural Disclaimer Policy</a>.</p>
        </>
      }
      sections={SECTIONS}
    />
  );
}
